import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { notification, Empty, Row, Col, Switch } from "antd";
import { formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import {
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetPageCounter,
  setWalletAsMain,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase
} from "../../redux/actions/merchantWallets";
import { useLocation } from 'react-router-dom';
import { getMerchantBalances, getMerchantData } from '../../redux/actions/merchantData';
import WalletsFilters from './WalletsFilters';
import WalletsPagination from './WalletsPagination';
import ReplenishModal from '../ReplenishModal/ReplenishModal';
import PrivateKeyModal from './PrivateKeyModal';
import WalletItem from "./WalletItem";
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './WalletsList.scss';
import { getMoreTransactions, getTransactions } from '../../redux/actions/transactions';
import {
  checkAutosignStatus, checkPasswordStatus,
  downloadAutosign,
  getWithdrawWallets,
  setPasswordSignature
} from "../../redux/actions/withdraw";
import useWindowSize from "../../utils/resizeHook";

const safeJsonParse = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const WalletsList = ({
  formName,
  merchantId,
  networkFilter,
  wallets,
  typeWallet,
  currencies,
  getMerchantWallets,
  getMoreMerchantWallets,
  page,
  pages,
  countItem,
  resetPageCounter,
  walletsFilters,
  fetching,
  getMerchantData,
  merchantData,
  deleteMerchantWallet,
  setWalletAsMain,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getMerchantBalances,
  getWithdrawWallets,
  autosignStep,
  downloadAutosignStep,
  downloadAutosign,
  setPasswordSignature,
  autosignStatus,
  checkAutosignStatus,
  checkPasswordStatus,
  passwordStatus,
  downloadError,
  getPassphrase
}) => {
  const [socket, setSocket] = useState(undefined);
  const [walletId, setWalletId] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletCurrency, setWalletCurrency] = useState('');
  const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const { t } = useTranslation();
  const [showWalletBy, setShowWalletBy] = useState(null);
  const [isSegwit, setIsSegwit] = useState(false);
  const [hideZeroCheckbox, setHideZeroCheckbox] = useState(
    safeJsonParse('cpayHideZeroBalancesClient', false)
  );
  const [adminWalletVersion, setAdminWalletVersion] = useState('v1');

  const { currentWidth } = useWindowSize();

  const defaultFilterValues = (() => {
    try {
      if (!currencies || !Array.isArray(currencies)) {
        return { order: 'DESC', search: '', limit: 10, currencyIds: [], typeNetwork: networkFilter };
      }
      return {
        order: 'DESC',
        search: '',
        limit: 10,
        currencyIds:
          typeWallet === 'merchant'
            ? currencies.filter(currency => currency.currencyType === 'currency').map(currency => currency._id)
            : currencies.map(currency => currency._id),
        typeNetwork: networkFilter,
      };
    } catch (e) {
      return { order: 'DESC', search: '', limit: 10, currencyIds: [], typeNetwork: networkFilter };
    }
  })();

  const { pathname } = useLocation();
  const isFromMerchantsWallets = pathname.includes('/merchants/wallets') || pathname === '/wallets';
  const walletVersionStorageKey = merchantId ? `adminWalletVersion:${merchantId}` : 'adminWalletVersion';
  const selectedWalletVersion =
    isFromMerchantsWallets
      ? (pathname === '/wallets' ? 'v1' : adminWalletVersion)
      : localStorage.getItem('walletVersion');
  const canSwitchWalletVersion =
    isFromMerchantsWallets &&
    pathname !== '/wallets' &&
    merchantData?.settingWallet?.version === 'v2' &&
    merchantData?.settingWallet?.defaultSetup === 'v1';

  const walletsToUse = wallets?.map(wallet => {
    if (wallet.balances) {
      const nativeIndex = wallet.balances.findIndex(b => b.currency.currencyType === 'currency');
      const primaryIndex = nativeIndex >= 0 ? nativeIndex : 0;
      const primary = wallet.balances[primaryIndex];
      const tokens = wallet.balances
        .filter((_, i) => i !== primaryIndex)
        .map(b => ({
          currency: b.currency.name,
          currencyId: b.currency.id,
          balance: { value: b.balance.value, usd: b.balance.usd },
        }));
      return {
        _id: wallet._id,
        address: wallet.address,
        currency: primary.currency.name,
        currencyId: primary.currency.id,
        currencyType: primary.currency.currencyType,
        nodeType: primary.currency.nodeType,
        balance: { value: primary.balance.value, usd: primary.balance.usd },
        typeWallet: wallet.typeWallet,
        main: wallet.main,
        tokens,
        createdAt: wallet.createdAt,
        isSegwit: wallet.isSegwit,
      };
    }
    return wallet;
  });

  useEffect(() => {
    if (merchantId) {
      getMerchantData(merchantId);
    }
  }, [merchantId]);

  useEffect(() => {
    if (isFromMerchantsWallets && merchantId && merchantData?.settingWallet?.version) {
      const storedVersion = localStorage.getItem(walletVersionStorageKey);
      const nextVersion = storedVersion || merchantData?.settingWallet?.version;
      setAdminWalletVersion(nextVersion);
    }
  }, [isFromMerchantsWallets, merchantId, merchantData?.settingWallet?.version]);

  useEffect(() => {
    if (isFromMerchantsWallets && merchantId && adminWalletVersion) {
      localStorage.setItem(walletVersionStorageKey, adminWalletVersion);
    }
  }, [isFromMerchantsWallets, merchantId, adminWalletVersion]);

  useEffect(() => {
    try {
      if (
        walletsFilters &&
        typeof walletsFilters === 'object' &&
        !Array.isArray(walletsFilters) &&
        merchantId &&
        merchantId.length
      ) {
        if (walletsFilters.limit || walletsFilters.order) {
          submitFilters();
        }
      }
    } catch (e) {
      console.error('submitFilters useEffect error:', e);
    }
  }, [merchantId, walletsFilters?.limit, walletsFilters?.order, networkFilter, adminWalletVersion]);

  const handleReplenishButton = (address, currency, isModalOpen) => {
    setWalletAddress(address);
    setWalletCurrency(currency);
    setIsReplenishModalOpen(isModalOpen);
  };

  const openNotification = () => {
    notification.success({
      message: t('copied'),
      duration: 2,
    });
  };

  const openModal = walletId => {
    setWalletId(walletId);
    setIsKeyModalOpen(true);
  };

  const closeModal = () => {
    setIsKeyModalOpen(false);
    resetTwoFaState(false);
  };

  const loadMoreWallets = page => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      console.log('Setting walletVersion in getMoreMerchantWallets payload (loadMoreWallets), walletVersion:', selectedWalletVersion);
      getMoreMerchantWallets(merchantId, {
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: page,
        typeNetwork: networkFilter,
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
        ...(isFromMerchantsWallets && { walletVersion: selectedWalletVersion }),
      });
    } catch (e) {
      console.error('loadMoreWallets error:', e);
    }
  };

  const updateList = () => {
    submitFilters()
  }

  const updateAfterChangeSegwit = () => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      resetPageCounter();
      console.log('Setting walletVersion in getMerchantWallets payload (updateAfterChangeSegwit), walletVersion:', selectedWalletVersion);
      getMerchantWallets(merchantId, {
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: page,
        typeNetwork: networkFilter,
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
        ...(isFromMerchantsWallets && { walletVersion: selectedWalletVersion }),
      }).then(() => setIsFetched(true));
    } catch (e) {
      console.error('updateAfterChangeSegwit error:', e);
    }
  }

  useEffect(() => {
    if (isSegwit) {
      updateAfterChangeSegwit()
    }

    setIsSegwit(false);
  }, [isSegwit])

  const submitFilters = () => {
    try {
      const { order, limit, search, currencyIds } = walletsFilters || {};
      resetPageCounter();
      console.log('Setting walletVersion in getMerchantWallets payload (submitFilters), walletVersion:', selectedWalletVersion);
      getMerchantWallets(merchantId, {
        order: order,
        limit: limit,
        search: (search || '').trim(),
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: 1,
        typeNetwork: networkFilter,
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
        ...(isFromMerchantsWallets && { walletVersion: selectedWalletVersion }),
      }).then(() => setIsFetched(true));
    } catch (e) {
      console.error('submitFilters error:', e);
    }
  };

  const resetFilters = () => {
    try {
      const { currencyIds } = defaultFilterValues || {};
      const safeDefaultFilterValues = defaultFilterValues || {};
      console.log('Setting walletVersion in getMerchantWallets payload (resetFilters), walletVersion:', selectedWalletVersion);
      getMerchantWallets(merchantId, {
        ...safeDefaultFilterValues,
        currencyIds: currencyIds?.join(',') || undefined,
        typeWallet: typeWallet,
        page: 1,
        typeNetwork: networkFilter,
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
        ...(isFromMerchantsWallets && { walletVersion: selectedWalletVersion }),
      }).then(() => setIsFetched(true));
    } catch (e) {
      console.error('resetFilters error:', e);
    }
  };

  useEffect(() => {
    socket && socket.disconnect();
    setSocket(undefined);
    createSocket(merchantId);
  }, [merchantId]);

  const createSocket = merchantId => {
    const io = window.io;
    const socket = io(process.env.REACT_APP_API_URL, {
      allowEIO3: true,
      withCredentials: true,
    });
    socket.on('connect', () => {
      socket.emit('sign-in-merchant', { token: localStorage.getItem('authToken'), merchantId });
      setSocket(socket);
    });
    socket.on('updatedBalance', () => {
     // const { currencyIds } = walletsFilters;

      if (merchantId) {
      //  getMerchantBalances(merchantId, networkFilter, true, false, walletVersion)
      //  getMerchantWallets(merchantId, {
      //   ...defaultFilterValues,
      //   currencyIds: currencyIds.join(',') || undefined,
      //   typeWallet: typeWallet,
      //   page: 1,
      //   typeNetwork: networkFilter,
      //   showTokens: false,
      // }, true).then(() => setIsFetched(true));
      // getWithdrawWallets(merchantId, currencyIds.join(',') || undefined, networkFilter, true, true);
      }
    });
    socket.on('disconnect', () =>{});
  };

  return (
    <React.Fragment>
      <ReplenishModal
        walletAddress={walletAddress}
        walletCurrency={walletCurrency}
        isReplenishModalOpen={isReplenishModalOpen}
        handleReplenishButton={handleReplenishButton}
      />
      <PrivateKeyModal
        merchantId={merchantId}
        walletId={walletId}
        setWalletId={setWalletId}
        isKeyModalOpen={isKeyModalOpen}
        closeModal={closeModal}
        openNotification={openNotification}
        getWalletPrivateKey={getWalletPrivateKey}
        showWalletBy={showWalletBy}
        getWalletMnemonicPhrase={getWalletMnemonicPhrase}
      />
      {currencies && currencies.length && (
        <>
          {canSwitchWalletVersion && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8, alignItems: 'center' }}>
              <span>Use Wallets 2.0 by default</span>
              <Switch
                checked={adminWalletVersion !== 'v1'}
                onChange={(value) => setAdminWalletVersion(value ? 'v2' : 'v1')}
              />
            </div>
          )}
          <WalletsFilters
            formName={formName}
            typeWallet={typeWallet}
            title={typeWallet === 'user' ? 'Clients Merchant' : 'Merchant'}
            currencies={currencies}
            merchantName={merchantData.name}
            initialValues={defaultFilterValues}
            submitFilters={submitFilters}
            resetFilters={resetFilters}
            fetching={fetching}
            hideZeroCheckbox={hideZeroCheckbox}
            setCheckbox={setHideZeroCheckbox}
          />
        </>
      )}
      {isFetched && walletsToUse && walletsToUse.length > 0 ? (
        <>
          <div className={`wallets-page ${fetching ? 'wallets-page_fetching' : ''}`}>
            {walletsToUse.map((wallet, index) => {
              return (
                <>
                  {index === 0 && (
                    <Row align="middle">
                      <Col span={1}>
                      </Col>
                      <Col sm={{ span: currentWidth >= 1024 ? 1 : 2, offset: 0 }} xs={{ span: 3, offset: 1 }}>
                      </Col>
                      <Col sm={{ span: currentWidth >= 1024 ? 7 : 8, offset: 0 }} xs={{ span: 17 }}>
                      </Col>
                      <Col sm={{ span: currentWidth >= 1024 ? 2 : 7, offset: 1 }} xs={{ span: 11, offset: 5 }}>
                      </Col>
                      <Col sm={{ span: currentWidth >= 1024 ? 2 : 5, offset: 0 }} xs={{ span: 8 }}>
                      </Col>
                      <Col
                        sm={{ span: currentWidth >= 1024 ? 3 : 10, offset: currentWidth >= 1024 ? 0 : 0 }}
                        xs={{ span: 10, offset: 5 }}
                      >
                      </Col>
                      {wallet.typeWallet === 'merchant' && (
                        <Col
                          sm={{ span: currentWidth >= 1024 ? 1 : 1, offset: currentWidth >= 1024 ? 1 : 2 }}
                          xs={{ span: 10, offset: 5 }}
                          style={{ marginLeft: '2%', textAlign: 'center', maxWidth: 'auto !important' }}
                        >
                          {t('merchants.security.autosignStatus')}
                        </Col>
                      )}
                      {wallet.typeWallet === 'merchant' && (
                        <Col
                          sm={{ span: currentWidth >= 1024 ? 1 : 1, offset: currentWidth >= 1024 ? 1 : 2 }}
                          xs={{ span: 10, offset: 5 }}
                          style={{ marginLeft: '4%', textAlign: 'center', maxWidth: 'auto !important' }}
                        >
                          {t('merchants.security.passwordStatus')}
                        </Col>
                      )}
                      <Col
                        sm={{ span: currentWidth >= 1024 ? 2 : 2, offset: currentWidth >= 1024 ? 0 : 6 }}
                        xs={{ span: 4, offset: 1 }}
                      >
                      </Col>
                      <Col
                        sm={{ span: currentWidth >= 1024 ? 1 : 1, offset: currentWidth >= 1024 ? 0 : 1 }}
                        xs={{ span: 1, offset: 1 }}
                      >
                      </Col>
                    </Row>
                  )}
                  <WalletItem
                    autosignStep={autosignStep}
                    downloadAutosignStep={downloadAutosignStep}
                    key={wallet._id}
                    merchantId={merchantId}
                    wallet={wallet}
                    typeWallet={typeWallet}
                    page={page}
                    pages={pages}
                    countItem={countItem}
                    downloadAutosign={downloadAutosign}
                    walletsFilters={walletsFilters}
                    openModal={openModal}
                    setPasswordSignature={setPasswordSignature}
                    autosignStatus={autosignStatus}
                    openNotification={openNotification}
                    deleteMerchantWallet={deleteMerchantWallet}
                    handleReplenishButton={handleReplenishButton}
                    setWalletAsMain={setWalletAsMain}
                    checkAutosignStatus={checkAutosignStatus}
                    checkPasswordStatus={checkPasswordStatus}
                    passwordStatus={passwordStatus}
                    currencies={currencies}
                    networkFilter={networkFilter}
                    getWalletPrivateKey={getWalletPrivateKey}
                    getWalletMnemonicPhrase={getWalletMnemonicPhrase}
                    setShowWalletBy={setShowWalletBy}
                    isFromMerchantsWallets={isFromMerchantsWallets}
                    resetTwoFaState={resetTwoFaState}
                    updateList={updateList}
                    downloadError={downloadError}
                    setIsSegwit={setIsSegwit}
                    getPassphrase={getPassphrase}
                    walletVersion={selectedWalletVersion}
                  />
                </>
              );
            })}
          </div>
          {!fetching && page && pages ? (
            <WalletsPagination
              page={page}
              pages={pages}
              walletsFilters={walletsFilters}
              loadMoreWallets={loadMoreWallets}
              submitFilters={submitFilters}
              formName={formName}
            />
          ) : null}
        </>
      ) : (
        !fetching && <Empty description={t('nodata')} />
      )}

      {fetching && (
        <div className="wallets__loader">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state, props) => ({
  wallets: state.merchantWallets.data,
  page: state.merchantWallets.page,
  pages: state.merchantWallets.pages,
  countItem: state.merchantWallets.countItem,
  fetching: state.merchantWallets.fetching,
  merchantData: state.merchantData.data,
  currencies: state.currencies.data,
  walletsFilters: formValueSelector(props.formName)(state, 'search', 'order', 'limit', 'currencyIds'),
  merchantId: state.transactions.merchantId,
  autosignStep: state.withdraw.autosignStep,
  downloadAutosignStep: state.withdraw.downloadAutosignStep,
  downloadError: state.withdraw.downloadError,
  autosignStatus: state.withdraw.autosignStatus,
  passwordStatus: state.withdraw.passwordStatus,
});

const mapDispatchToProps = dispatch => ({
  getMerchantWallets: getMerchantWallets(dispatch),
  getMoreMerchantWallets: getMoreMerchantWallets(dispatch),
  getMerchantData: getMerchantData(dispatch),
  deleteMerchantWallet: deleteMerchantWallet(dispatch),
  setWalletAsMain: setWalletAsMain(dispatch),
  resetTwoFaState: resetTwoFaState(dispatch),
  getWalletPrivateKey: getWalletPrivateKey(dispatch),
  resetPageCounter,
  getWalletMnemonicPhrase: getWalletMnemonicPhrase(dispatch),
  getTransactions: getTransactions(dispatch),
  getMoreTransactions: getMoreTransactions(dispatch),
  getMerchantBalances: getMerchantBalances(dispatch),
  getWithdrawWallets: getWithdrawWallets(dispatch),
  downloadAutosign: downloadAutosign(dispatch),
  setPasswordSignature: setPasswordSignature(dispatch),
  checkAutosignStatus: checkAutosignStatus(dispatch),
  checkPasswordStatus: checkPasswordStatus(dispatch),
  getPassphrase: getPassphrase(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(WalletsList);
